/**
 * Canonical beta experience roster.
 *
 * Single source of truth for which lessons and modules are in the beta
 * launch path. Captions, beta path UI, QA reporting, admin tools, and
 * analytics should all reference this file instead of maintaining their
 * own allowlists.
 *
 * IMPORTANT: do NOT derive the beta roster from `lessons.is_beta_content`,
 * course membership, concept tags, related lessons, or module reinforcement
 * concepts. Related does not mean included. Only the entries below are in
 * the agreed beta experience.
 *
 * The three beta modules use the separate instructor-voice caption system,
 * NOT lesson-video captions. Keep them excluded from caption discovery.
 *
 * The Supabase edge function `generate-lesson-captions` mirrors these
 * lesson IDs in `BETA_LESSON_ROSTER` because Deno functions cannot import
 * from `src/`. If you change this file, update that function too.
 */

export const launchLessonTitles = [
  "Needs vs Wants",
  "Opportunity Cost",
  "Financial Tradeoffs",
  "Delayed Gratification",
  "Cash Flow",
  "Credit Score Basics",
  "Borrowing Money",
  "Minimum Payments Trap",
  "Urgency as a Red Flag",
  "Too-Good-To-Be-True Returns",
] as const;

export type BetaLessonTitle = (typeof launchLessonTitles)[number];

/** Maps DB lesson_name -> learner-facing beta display title. */
export const betaDisplayTitleMap: Record<string, string> = {
  "Too-Good-To-Be-True Returns": "Too Good to Be True",
};

export const betaModules = [
  "Budget Builder Module",
  "Credit Management Module",
  "Scam Defense Module",
] as const;

export type BetaModuleTitle = (typeof betaModules)[number];

/**
 * Stable lesson_id allowlist mirrored from the edge function. Kept here so
 * client code (admin tools, QA dashboards, analytics) can match against IDs
 * without round-tripping through lesson_name lookups.
 *
 * NOTE on the "10 vs 13" question (Phase 2B reconciliation):
 * The official beta path is 13 steps = these 10 curated lessons + the 3
 * beta modules (Budget Builder, Credit Management, Scam Defense). Modules
 * are NOT lessons and intentionally do not appear in this lesson_id list —
 * the canonical 13-step ordering lives in `src/lib/launchPath.ts`
 * (LAUNCH_PATH) and is enforced by `src/test/launchPath.test.ts`.
 * If you need the full 13-step sequence (lesson + module), import from
 * launchPath. If you need to know whether a `lesson_id` is part of the
 * beta path's lesson set, use `isLaunchLessonId` below.
 */
export const launchLessonIds = [
  "709b0a91-2103-4ddf-9a70-e4ee5dae0b41", // Needs vs Wants
  "f1ec70b9-2619-41b4-b2e8-f89425bf4e1b", // Opportunity Cost
  "9979b6d9-b308-49f4-b9a5-29e5a6a7ff3f", // Financial Tradeoffs
  "e29e634d-e035-42fb-8568-a5241a02d595", // Delayed Gratification
  "d2b87cb1-7774-4029-87e0-be51ab14840e", // Cash Flow
  "c6d62ce8-0b9e-40ff-b09d-64bcda1f6210", // Credit Score Basics
  "db6fdc3b-f16d-4fe5-a750-c65c2c8dc5a1", // Borrowing Money
  "fcbdef95-7194-4921-8cb7-dfdfebda0ee0", // Minimum Payments Trap
  "e1e29e49-0074-48f5-ba15-70374284867e", // Urgency as a Red Flag
  "846aa833-7dd2-4059-892a-6fbebf6a2c2e", // Too-Good-To-Be-True Returns
] as const;

/** Lessons explicitly NOT in beta even though they share courses/concepts. */
export const excludedNonBetaLessonTitles = [
  "Scam Psychology",
  "Good Debt vs Bad Debt",
  "Verification Habits",
] as const;

export const BETA_LESSON_COUNT = launchLessonTitles.length;

export function getLaunchDisplayTitle(dbLessonName: string): string {
  return betaDisplayTitleMap[dbLessonName] ?? dbLessonName;
}

export function isLaunchLessonId(lessonId: string): boolean {
  return (launchLessonIds as readonly string[]).includes(lessonId);
}

export function isBetaModuleTitle(title: string): boolean {
  return (betaModules as readonly string[]).includes(title);
}